"use client"

import React from "react"
import { Badge } from "@/components/ui/badge"
import { TableRow, TableCell } from "@/components/ui/table"
import {
  Phone,
  Clock,
  Calendar,
  PhoneIncoming,
  PhoneOutgoing,
  CheckCircle,
  XCircle,
  AlertCircle,
  Settings,
  UserX,
  Bot,
  Users,
  MessageSquare,
} from "lucide-react"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { AudioPlayer } from "@/components/ui/media/audio-player"
import { CallSummary } from "./call-summary"
import { CallActionsMenu, type CallActionsMenuProps } from "./call-actions-menu"
import { CallTypeBadge } from "./journey-badges"
import type { ServiceType, TranscriptMessage } from "@/types/calls"
import { useCreditFormatters } from '@/hooks/use-credit-formatters'
import { useLocale, useTranslations } from "next-intl"
import { TranslationText } from "@/components/i18n/translation-text"





function formatCallIdShort(id: string): string {
  // Keep prefix (va_ or pbx_) and show last 3 chars of the rest
  const prefixMatch = id.match(/^(va_|pbx_)(.+)$/)
  if (prefixMatch) {
    const [, prefix, rest] = prefixMatch
    if (rest.length <= 3) return id
    return `${prefix}..${rest.slice(-3)}`
  }
  // Fallback for IDs without prefix
  if (id.length <= 6) return id
  return `..${id.slice(-3)}`
}

function getStatusBadge(status: string, callType: "voice_agent" | "pbx", tUi: ReturnType<typeof useTranslations<"Calls.CallsCallRow">>) {
  const normalizedStatus = status.toLowerCase()

  // Voice agent statuses
  if (callType === "voice_agent") {
    switch (normalizedStatus) {
      case "done":
        return (
          <Badge className="bg-green-100 text-green-800">
            <CheckCircle className="w-3 h-3 mr-1" />
            <span><span key={tUi("completed")}>{tUi("completed")}</span></span>
          </Badge>
        )
      case "failed":
        return (
          <Badge className="bg-red-100 text-red-800">
            <XCircle className="w-3 h-3 mr-1" />
            <span><span key={tUi("failed")}>{tUi("failed")}</span></span>
          </Badge>
        )
      case "in_progress":
        return (
          <Badge className="bg-yellow-100 text-yellow-800">
            <AlertCircle className="w-3 h-3 mr-1" />
            <span><span key={tUi("inProgress")}>{tUi("inProgress")}</span></span>
          </Badge>
        )
      default:
        return <Badge variant="secondary">{status}</Badge>
    }
  }

  // PBX statuses
  switch (normalizedStatus) {
    case "completed":
      return (
        <Badge className="bg-green-100 text-green-800">
          <CheckCircle className="w-3 h-3 mr-1" />
          <span><span key={tUi("completed")}>{tUi("completed")}</span></span>
        </Badge>
      )
    case "failed":
      return (
        <Badge className="bg-red-100 text-red-800">
          <XCircle className="w-3 h-3 mr-1" />
          <span><span key={tUi("failed")}>{tUi("failed")}</span></span>
        </Badge>
      )
    case "in-progress":
      return (
        <Badge className="bg-yellow-100 text-yellow-800">
          <AlertCircle className="w-3 h-3 mr-1" />
          <span><span key={tUi("inProgress")}>{tUi("inProgress")}</span></span>
        </Badge>
      )
    default:
      return <Badge variant="secondary">{status}</Badge>
  }
}

function getServiceBadge(serviceType: ServiceType) {
  switch (serviceType) {
    case "elevenlabs":
      return (
        <Badge className="bg-purple-100 text-purple-800">
          <Settings className="w-3 h-3 mr-1" />
          <span><span key={"ElevenLabs"}>{"ElevenLabs"}</span></span>
        </Badge>
      )
    case "blandai":
      return (
        <Badge className="bg-blue-100 text-blue-800">
          <Settings className="w-3 h-3 mr-1" />
          <span><span key={"Bland AI"}>{"Bland AI"}</span></span>
        </Badge>
      )
    case "vapi":
      return (
        <Badge className="bg-orange-100 text-orange-800">
          <Settings className="w-3 h-3 mr-1" />
          <span><span key={"Vapi"}>{"Vapi"}</span></span>
        </Badge>
      )
    default:
      return <Badge variant="secondary">{serviceType}</Badge>
  }
}

function getDirectionIcon(direction: "inbound" | "outbound", tUi: ReturnType<typeof useTranslations<"Calls.CallsCallRow">>) {
  return direction === "inbound" ? (
    <div className="flex items-center gap-1 text-blue-600">
      <PhoneIncoming className="w-4 h-4" />
      <span className="text-xs"><span key={tUi("inbound")}>{tUi("inbound")}</span></span>
    </div>
  ) : (
    <div className="flex items-center gap-1 text-green-600">
      <PhoneOutgoing className="w-4 h-4" />
      <span className="text-xs"><span key={tUi("outbound")}>{tUi("outbound")}</span></span>
    </div>
  )
}

export function CallRow({
  call,
  isSuperUser,
  canDelete,
  onViewTranscript,
  onViewDetails,
  onViewToolCalls,
  onDelete,
  feedbackStatus,
  onAddFeedback,
}: CallActionsMenuProps) {


  const tUi = useTranslations("Calls.CallsCallRow")
  const locale = useLocale()
  const { formatCredits } = useCreditFormatters()
  const formatDate = (value: string) => new Date(value).toLocaleDateString(locale, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })
  const formatDuration = (seconds: number | null) => seconds ? tUi("durationValue", { minutes: Math.floor(seconds / 60), seconds: seconds % 60 }) : tUi("notAvailable")
  const formatCost = (amount: number | null | undefined) => amount ? formatCredits(amount) : tUi("notAvailable")

  const isVoiceAgent = call.type === "voice_agent"
  const isPbx = call.type === "pbx"

  // Get agent info
  const voiceAgent = call.voiceAgent
  const agentStatus = voiceAgent?.status
  const isAgentInactive = agentStatus && agentStatus !== "active"

  // Total columns based on user role
  const totalCols = isSuperUser ? 13 : 10

  // Agent-work cost (microUSD): a voice-agent call bills directly; a PBX call
  // bills through its linked AI segment (N/A when the call never used an agent).
  const cost = isVoiceAgent
    ? call.voiceAgentData?.chargeMicrousd
    : call.pbxData?.linkedAiCall?.chargeMicrousd

  // Get service type (only for voice agent calls)
  const serviceType = isVoiceAgent ? call.voiceAgentData?.serviceType : null

  // Get audio URL
  const audioUrl = isVoiceAgent
    ? call.voiceAgentData?.audioUrl
    : call.pbxData?.recordingUrl

  // Get conversation ID for audio player
  const conversationId = isVoiceAgent
    ? call.voiceAgentData?.conversationId
    : call.pbxData?.twilioCallSid

  // Messages count (only real messages with text, not tool calls)
  const getActualMessagesCount = (transcript: TranscriptMessage[] | undefined | null): number => {
    if (!transcript) return 0
    return transcript.filter(msg => msg.message && msg.message.trim() !== '').length
  }

  const messagesCount = isVoiceAgent
    ? getActualMessagesCount(call.voiceAgentData?.transcript)
    : getActualMessagesCount(call.pbxData?.linkedAiCall?.transcript)

  // Transcript summary
  const transcriptSummary = isVoiceAgent
    ? call.voiceAgentData?.transcriptSummary
    : call.pbxData?.linkedAiCall?.transcriptSummary

  return (
    <React.Fragment>
      {/* Main row */}
      <TableRow className="hover:bg-gray-50">
        {/* Call ID */}
        <TableCell className="font-medium">
          <Tooltip>
            <TooltipTrigger asChild className="cursor-help">
              <button type="button" className="inline-flex whitespace-nowrap text-left" aria-label={tUi("callIDValue0", { value0: String(call.id) })}>
                {formatCallIdShort(call.id)}
              </button>
            </TooltipTrigger>
            <TooltipContent className="max-w-[calc(100vw-2rem)] break-all">{call.id}</TooltipContent>
          </Tooltip>
        </TableCell>

        {/* Type */}
        <TableCell>
          <CallTypeBadge
            type={call.type}
            hasLinkedAiCall={isPbx && !!call.pbxData?.linkedAiCall}
          />
        </TableCell>

        {/* Service (super user only, voice agent only) */}
        {isSuperUser && (
          <TableCell>
            {serviceType ? getServiceBadge(serviceType) : <span className="text-gray-400">-</span>}
          </TableCell>
        )}

        {/* Direction */}
        {isSuperUser && (
          <TableCell>
            {getDirectionIcon(call.direction, tUi)}
          </TableCell>
        )}

        {/* Agent / Call Flow */}
        <TableCell>
          {isVoiceAgent ? (
            voiceAgent ? (
              <div className="flex flex-col">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">{voiceAgent.name}</span>
                  {isAgentInactive && (
                    <Badge variant="outline" className="text-xs text-orange-600 border-orange-300">
                      <span key={agentStatus === "disabled" ? tUi("disabled") : agentStatus === "draft" ? tUi("draft") : tUi("inactive")}>{agentStatus === "disabled" ? tUi("disabled") : agentStatus === "draft" ? tUi("draft") : tUi("inactive")}</span>
                    </Badge>
                  )}
                </div>
                {isSuperUser && voiceAgent.external_id && (
                  <span className="text-xs text-gray-500"><TranslationText>{tUi.rich("iDValue0", { value0: String(voiceAgent.external_id) })}</TranslationText></span>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-1.5 text-gray-400">
                <UserX className="w-4 h-4" />
                <span className="text-sm italic"><span key={tUi("agentDeleted")}>{tUi("agentDeleted")}</span></span>
              </div>
            )
          ) : (
            <div className="flex flex-col">
              <span className="text-sm font-medium">
                {call.pbxData?.callFlowName || tUi("pBXCall")}
              </span>
              {call.pbxData?.callSegment && call.pbxData.callSegment !== "main" && (
                <Badge variant="outline" className="text-xs mt-0.5 w-fit">
                  {call.pbxData.callSegment === "ai_agent" && <Bot className="w-3 h-3 mr-1" />}
                  {call.pbxData.callSegment === "operator" && <Users className="w-3 h-3 mr-1" />}
                  {call.pbxData.callSegment}
                </Badge>
              )}
            </div>
          )}
        </TableCell>

        {/* From Number */}
        <TableCell>
          <div className="flex items-center gap-1">
            <Phone className="w-3 h-3 text-gray-400" />
            <span className="text-sm">{call.callerNumber || "N/A"}</span>
          </div>
        </TableCell>

        {/* To Number */}
        <TableCell>
          <div className="flex items-center gap-1">
            <Phone className="w-3 h-3 text-gray-400" />
            <span className="text-sm">{call.calledNumber || "N/A"}</span>
          </div>
        </TableCell>

        {/* Status */}
        <TableCell>{getStatusBadge(call.status, call.type, tUi)}</TableCell>

        {/* Duration */}
        <TableCell>
          <div className="flex items-center gap-1">
            <Clock className="w-3 h-3 text-gray-400" />
            <span className="text-sm">{formatDuration(call.durationSeconds)}</span>
          </div>
        </TableCell>

        {/* Agent Cost (super user only) */}
        {isSuperUser && (
          <TableCell>
            <span className="text-sm">{formatCost(cost)}</span>
          </TableCell>
        )}

        {/* Date */}
        <TableCell>
          <div className="flex items-center gap-1">
            <Calendar className="w-3 h-3 text-gray-400" />
            <span className="text-sm">{formatDate(call.createdAt)}</span>
          </div>
        </TableCell>

        {/* Transcript */}
        <TableCell>
          {messagesCount > 0 ? (
            <button
              onClick={() => onViewTranscript(call.id)}
              className="flex items-center gap-1 hover:bg-gray-100 rounded px-1.5 py-0.5 -ml-1.5 transition-colors"
            >
              <MessageSquare className="w-3 h-3 text-gray-400" />
              <span className="text-xs text-gray-600 hover:text-gray-900"><TranslationText>{tUi.rich("value0Messages", { value0: String(messagesCount) })}</TranslationText></span>
            </button>
          ) : (
            <span className="text-xs text-gray-400">-</span>
          )}
        </TableCell>

        {/* Actions */}
        <TableCell mobileLabel="">
          <CallActionsMenu
            call={call}
            isSuperUser={isSuperUser}
            canDelete={canDelete}
            onViewTranscript={onViewTranscript}
            onViewDetails={onViewDetails}
            onViewToolCalls={onViewToolCalls}
            onDelete={onDelete}
            feedbackStatus={feedbackStatus}
            onAddFeedback={onAddFeedback}
          />
        </TableCell>
      </TableRow>

      {/* Audio player row (voice agent only) */}
      {isVoiceAgent && audioUrl && conversationId && (
        <TableRow mobileVariant="continuation">
          <TableCell colSpan={totalCols} className="p-0">
            <div className="sm:p-4 sm:pt-2">
              <AudioPlayer
                audioUrl={audioUrl}
                conversationId={conversationId}
                callId={typeof call.originalId === 'number' ? call.originalId : undefined}
                initialDuration={call.durationSeconds || undefined}
              />
            </div>
          </TableCell>
        </TableRow>
      )}

      {/* Audio players for PBX calls */}
      {isPbx && (call.pbxData?.linkedAiCall?.audioUrl || call.pbxData?.recordingUrl) && (
        <TableRow mobileVariant="continuation">
          <TableCell colSpan={totalCols} className="p-0">
            <div className="space-y-3 sm:p-4 sm:pt-2">
              {/* AI Agent audio (from linkedAiCall) */}
              {call.pbxData?.linkedAiCall?.audioUrl && (
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Bot className="w-4 h-4 text-purple-600" />
                    <span className="text-sm font-medium text-purple-700"><span key={tUi("aIAgentRecording")}>{tUi("aIAgentRecording")}</span></span>
                    {call.pbxData.linkedAiCall.agentName && (
                      <span className="text-xs text-gray-500">({call.pbxData.linkedAiCall.agentName})</span>
                    )}
                  </div>
                  <AudioPlayer
                    audioUrl={call.pbxData.linkedAiCall.audioUrl}
                    conversationId={call.pbxData.linkedAiCall.conversationId}
                    callId={call.pbxData.linkedAiCall.id}
                    initialDuration={call.pbxData.linkedAiCall.duration || undefined}
                  />
                </div>
              )}
              {/* Operator recording (from PBX) */}
              {call.pbxData?.recordingUrl && (
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Users className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-medium text-blue-700"><span key={tUi("operatorRecording")}>{tUi("operatorRecording")}</span></span>
                    {call.pbxData.answeredByNumber && (
                      <span className="text-xs text-gray-500">({call.pbxData.answeredByNumber})</span>
                    )}
                  </div>
                  <AudioPlayer
                    audioUrl={call.pbxData.recordingUrl}
                    conversationId={call.pbxData.twilioCallSid}
                    pbxCallId={call.pbxData.id}
                  />
                </div>
              )}
            </div>
          </TableCell>
        </TableRow>
      )}


      {/* Summary row */}
      {transcriptSummary && (
        <TableRow mobileVariant="continuation">
          <TableCell colSpan={totalCols} className="p-0">
            <div className="min-w-0 sm:w-0 sm:min-w-full sm:p-4">
              <CallSummary summary={transcriptSummary} />
            </div>
          </TableCell>
        </TableRow>
      )}

    </React.Fragment>
  )
}
